import _, { add } from 'lodash';
import { TaxRateSelector, ITaxRate, ITaxType } from './taxselector';
import { PriceListSelector, PriceListInput, IPriceList } from './pricelistselector';
import { PriceSelector, PriceSelectorInput, IPriceSelector, IPriceRule, PriceSelectorOutput } from './priceselector';
import { roundPrice } from '../utils/round';
import { IProductDetails, ProductTextSelector } from './producttextselector';

export const NO_PRICE = -100000;

export const EContent_HotelServiceTypeAssignment = {
  SMALL: 'SMALL',
  MEDIUM: 'MEDIUM',
  REGULAR: 'REGULAR',
};
export const EPricing_ProductLookupCode = {
  ROOM_DZ: 'ROOM_DZ',
  ROOM_EZ: 'ROOM_EZ',
  VOLLPENSIONSPAUSCHALE_DZ: 'VOLLPENSIONSPAUSCHALE_DZ',
  VOLLPENSIONSPAUSCHALE_EZ: 'VOLLPENSIONSPAUSCHALE_EZ',
  ADDON_COFFEE: 'ADDON_COFFEE',
  ADDON_DINNER: 'ADDON_DINNER',
  ADDON_LUNCH: 'ADDON_LUNCH',
  ADDON_MEALDRINK: 'ADDON_MEALDRINK',
  ADDON_SEMINARDRINK: 'ADDON_SEMINARDRINK',
  ADDON_COFFEEBREAK: 'ADDON_COFFEEBREAK',
  DEDUCTION_DINNER: 'DEDUCTION_DINNER',
  DEDUCTION_LUNCH: 'DEDUCTION_LUNCH',
  DEDUCTION_PAUSE: 'DEDUCTION_PAUSE',
  DEDUCTION_SEMIDAY: 'DEDUCTION_SEMIDAY',
  HALBTAGESPAUSCHALE: 'HALBTAGESPAUSCHALE',
  SEMINARPAUSCHALE_DZ: 'SEMINARPAUSCHALE_DZ',
  SEMINARPAUSCHALE_EZ: 'SEMINARPAUSCHALE_EZ',
  SEMINARROOM_FULLDAY: 'SEMINARROOM_FULLDAY',
  SEMINARROOM_SEMIDAY: 'SEMINARROOM_SEMIDAY',
  TAGESPAUSCHALE: 'TAGESPAUSCHALE',
  VISITOR_TAX: 'VISITOR_TAX',
  VORTAGSANREISE_DZ: 'VORTAGSANREISE_DZ',
  VORTAGSANREISE_EZ: 'VORTAGSANREISE_EZ',
  ABREISEPAUSCHALE: 'ABREISEPAUSCHALE',
} as const;

const FULL_BOARD_COMBINATION_LOOKUP_CODES: string[] = [
  EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ,
  EPricing_ProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ,
];

export const EPricing_ProductPayment = {
  BYGUESTANDDAY: 'BYGUESTANDDAY',
  BYCOUNT: 'BYCOUNT',
  BYGUEST: 'BYGUEST',
  BYROOMANDDAY: 'BYROOMANDDAY',
  BYROOM: 'BYROOM',
};
export const EPricing_FacilityPayment = {
  BYCOUNT: 'BYCOUNT',
  BYDAY: 'BYDAY',
  BYGUEST: 'BYGUEST',
};
export const EPricing_FacilityLookupCode = {
  SEMINARROOM_FULLDAY: 'SEMINARROOM_FULLDAY',
  SEMINARROOM_SEMIDAY: 'SEMINARROOM_SEMIDAY',
};
export const EWL_OfferRoomOccupancy = {
  FULLDAY: 'FULLDAY',
  MORNING: 'MORNING',
  AFTERNOON: 'AFTERNOON',
};

export interface IQuickPriceInput<TQuickPriceDay extends IQuickPriceDay> {
  comment?: string | null;
  title?: string | null;
  days: TQuickPriceDay[];
  endDate: string;
  language: string;
  startDate: string;
  isPremium?: boolean;
  publicCompanyId?: number;
  publicClientId?: number;
}
export interface IQuickPriceAddon {
  count?: number | null;
  lookupCode?: string | null;
  sku?: string | null;
}
export interface IQuickPriceDay {
  addonFacilities?: IQuickPriceAddon[] | null;
  addonProducts?: IQuickPriceAddon[] | null;
  day: number;
  seating: string[];
  totalGuests: number;
}

export interface ICalcPriceOutput {
  priceList: IPriceList;
  hotel: IHotel;
  totalPriceNet: number;
  totalPriceGross: number;
  totalTaxes: number;
  taxes: ICalcTax[];
  lineItems: ICalcLineItem[];
}

export interface ICalcLineItem {
  day: number;
  sku: string;
  name: string;
  product?: IProduct | null;
  bundle?: IProductBundle | null;
  facility?: IFacility | null;
  selector?: IPriceSelector | null;
  isIncluded: boolean;
  count: number;
  priceItem: number;
  priceGross: number;
  priceNet: number;
  components: ICalcPriceComponent[];
  taxes: ICalcTax[];
  // Optional fields for seminar room details
  seatingType?: string | null;
  seatCount?: number | null;
  seminarRoomEquipment?: {
    beamerCount?: number | null;
    flipchartCount?: number | null;
    pinboardCount?: number | null;
    requireAirConditioning?: boolean | null;
    requireDaylight?: boolean | null;
  } | null;
  /** Free-text layout when seatingType is OTHER. */
  seatingDescription?: string | null;
}

export interface ICalcPriceComponent {
  type: ITaxType;
  price: number;
}

export interface ICalcTax {
  type: ITaxType;
  rate: ITaxRate;
  price: number;
}

export interface IHotel {
  id: number;
  businessCountry: string;
}

export interface IPrice {
  price: number | null;
  bundlePriceFromProduct?: boolean | null;
  components?: IPriceComponent[];
  selector?: IPriceSelector | null;
}
export interface IPriceComponent {
  taxTypeId: number;
  price: number;
}

export interface IFacility {
  id: number;
  name: string;
  sku: string;
  lookupCode: string | null;
  recurring: string;
  components: IComponent[];
  meetingRoom?: {
    id: number;
    name: string;
    hotelId: number;
    area: number;
    capacityUForm?: number | null;
    capacityTheater?: number | null;
    capacityParlament?: number | null;
    capacityCircle?: number | null;
    capacityBankett?: number | null;
    capacityCocktail?: number | null;
    capacityBlock?: number | null;
  } | null;
}
export interface IProduct {
  id: number;
  name: string;
  sku: string;
  lookupCode: string | null;
  isDeduction: boolean;
  recurring: string;
  components: IComponent[];
}
export interface IComponent {
  taxTypeId: number;
}
export interface IId {
  id: number;
  sku: string;
}
export interface IProductBundle {
  id: number;
  name: string;
  sku: string;
  lookupCode: string | null;
  recurring: string;
  components: IComponent[];
  bundleItems: IProductBundleItem[];
}
export interface IProductBundleItem {
  productId: number;
  includedCount: number;
}

export interface IServiceType {
  id: number;
  name: string;
  sku: string;
  listerServiceTypeAssignment?: string;
  products: IServiceTypeProduct[];
  facilities: IServiceTypeFacility[];
  addons: IServiceTypeAddon[];
}
export interface IServiceTypeProduct {
  isForDayVisitor: boolean;
  isForDeparture: boolean;
  isForOvernight: boolean;
  isForSemidayVisitor: boolean;
  isForSemidayOvernight: boolean;
  isForSemidayDeparture: boolean;
  isForNextday: boolean;
  isForRoom: boolean;
  isForPrevday: boolean;
  bundle: IId | null;
  product: IId | null;
}
export interface IServiceTypeFacility {
  facility: IId;
  isForDay: boolean;
  isForSemiday: boolean;
  includedCount: number;
}
export interface IServiceTypeAddon {
  approvalLimit?: number | null;
  product: IId | null;
  facility: IId | null;
}
export interface ICalcOptions {
  addonsOnly?: boolean;
  allowNoPrices?: boolean;
  isPricesNet?: boolean;
  priceSelector?: (sel: PriceSelectorInput) => Omit<PriceSelectorOutput<IPriceSelector>, 'selector'> | null | undefined;
}

export class QuickPriceError extends Error {
  readonly extensions: any | undefined;
  constructor(message: string, extensions?: any | undefined) {
    super(message);
    this.extensions = extensions;
  }
}

/**
 * Merges taxes by tax type and keeps display ordering stable.
 */
export function mergeTaxes(destination: ICalcTax[], taxes: ICalcTax[]) {
  for (const tax of taxes) {
    const existingTax = destination.find(t => t.type.id === tax.type.id);
    if (existingTax) {
      if (existingTax.price === NO_PRICE || tax.price === NO_PRICE) existingTax.price = NO_PRICE;
      else existingTax.price += tax.price;
    } else {
      destination.push({ ...tax });
    }
  }
  return _.orderBy(destination, d => d.type.sequence, 'asc');
}
/**
 * Merges price components by tax type and keeps display ordering stable.
 */
export function mergeComponents(destination: ICalcPriceComponent[], components: ICalcPriceComponent[]) {
  for (const component of components) {
    const existingComponent = destination.find(t => t.type.id === component.type.id);
    if (existingComponent) {
      if (existingComponent.price === NO_PRICE || existingComponent.price === NO_PRICE) existingComponent.price = NO_PRICE;
      else existingComponent.price += component.price;
    } else {
      destination.push({ ...component });
    }
  }
  return _.orderBy(destination, d => d.type.sequence, 'asc');
}

/**
 * Merges a line item into the accumulated calculation result.
 */
export function mergeLineItem(result: ICalcPriceOutput, lineItem: ICalcLineItem, merge: 'ADD' | 'IGNORE' | 'NEW' = 'ADD') {
  const existingLineItem = result.lineItems.find(
    l =>
      ((l.day === null && lineItem.day === null) || l.day === lineItem.day) &&
      l.sku === lineItem.sku &&
      l.isIncluded === lineItem.isIncluded &&
      ((l.selector && lineItem.selector && l.selector.id === lineItem.selector.id) || (!l.selector && !lineItem.selector)),
  );

  if (existingLineItem) {
    if (merge === 'ADD') {
      existingLineItem.count += lineItem.count;
      if (existingLineItem.priceGross === NO_PRICE || lineItem.priceGross === NO_PRICE) existingLineItem.priceGross = NO_PRICE;
      else existingLineItem.priceGross += lineItem.priceGross;
      if (existingLineItem.priceNet === NO_PRICE || lineItem.priceNet === NO_PRICE) existingLineItem.priceNet = NO_PRICE;
      else existingLineItem.priceNet += lineItem.priceNet;
      mergeTaxes(existingLineItem.taxes, lineItem.taxes);
    } else if (merge === 'NEW') {
      result.lineItems.push({ ...lineItem });
    }
  } else {
    result.lineItems.push({ ...lineItem });
  }
}

/**
 * Recomputes total net/gross amounts and aggregated taxes.
 */
export function sumTotalPrices(result: ICalcPriceOutput) {
  result.totalPriceGross = result.lineItems.reduce((sum, li) => (sum === NO_PRICE || li.priceGross === NO_PRICE ? NO_PRICE : sum + li.priceGross), 0);
  result.totalPriceNet = result.lineItems.reduce((sum, li) => (sum === NO_PRICE || li.priceNet === NO_PRICE ? NO_PRICE : sum + li.priceNet), 0);
  result.taxes = [];
  for (const lineItem of result.lineItems) {
    mergeTaxes(result.taxes, lineItem.taxes);
  }
  result.totalTaxes =
    result.totalPriceGross === NO_PRICE || result.totalPriceNet === NO_PRICE ? NO_PRICE : result.totalPriceGross - result.totalPriceNet;
}

export abstract class QuickPriceCalculatorBase<
  TQuickPriceDay extends IQuickPriceDay,
  TQuickPriceInput extends IQuickPriceInput<TQuickPriceDay>,
  TCalcOptions extends ICalcOptions,
  TQuickPriceCalcOutput extends ICalcPriceOutput,
> {
  public taxRateSelector: TaxRateSelector<ITaxRate, ITaxType> | null = null;
  public productTextSelector: ProductTextSelector<IProductDetails> | null = null;

  public hotel: IHotel | null = null;

  public allProductsDb: IProduct[] | null = null;
  public allFacilitiesDb: IFacility[] | null = null;
  public allBundlesDb: IProductBundle[] | null = null;
  public allServiceTypesDb: IServiceType[] | null = null;

  public priceListSelector: PriceListSelector<IPriceList> | null = null;
  public priceSelector: PriceSelector<IPriceSelector, IPriceRule> | null = null;

  public includeMainRoomInPackage?: boolean;

  public hydrate(json: string) {
    const {
      allProductsDb,
      allFacilitiesDb,
      allBundlesDb,
      allServiceTypesDb,
      productTextSelector,
      hotel,
      priceListSelector,
      priceSelector,
      taxRateSelector,
      includeMainRoomInPackage,
    } = JSON.parse(json);

    this.taxRateSelector = new TaxRateSelector();
    if (taxRateSelector) {
      this.taxRateSelector.hydrate(taxRateSelector);
    }

    this.productTextSelector = new ProductTextSelector();
    if (productTextSelector) {
      this.productTextSelector.hydrate(productTextSelector);
    }

    this.hotel = hotel;

    this.allProductsDb = allProductsDb;
    this.allFacilitiesDb = allFacilitiesDb;
    this.allBundlesDb = allBundlesDb;
    this.allServiceTypesDb = allServiceTypesDb;

    this.priceListSelector = new PriceListSelector();
    if (priceListSelector) {
      this.priceListSelector.hydrate(priceListSelector);
    }
    this.priceSelector = new PriceSelector();
    if (priceSelector) {
      this.priceSelector.hydrate(priceSelector);
    }

    this.includeMainRoomInPackage = includeMainRoomInPackage;
  }
  public dehydrate(): string {
    return JSON.stringify({
      taxRateSelector: this.taxRateSelector?.dehydrate(),
      productTextSelector: this.productTextSelector?.dehydrate(),
      hotel: this.hotel,
      allProductsDb: this.allProductsDb,
      allFacilitiesDb: this.allFacilitiesDb,
      allBundlesDb: this.allBundlesDb,
      allServiceTypesDb: this.allServiceTypesDb,
      priceListSelector: this.priceListSelector?.dehydrate(),
      priceSelector: this.priceSelector?.dehydrate(),
      includeMainRoomInPackage: this.includeMainRoomInPackage,
    });
  }

  public getPriceList(input: PriceListInput) {
    const priceList = this.priceListSelector?.findPriceList(input);
    if (priceList) {
      return priceList;
    } else {
      throw new QuickPriceError(`No pricelist found for ${input.date}`, {
        extensions: { code: 'NO_PRICELIST' },
      });
    }
  }
  public getServiceType = (serviceTypeSku: string) => {
    const serviceType = this.allServiceTypesDb?.find(s => s.sku === serviceTypeSku);
    if (!serviceType) {
      throw new QuickPriceError(`Service type ${serviceTypeSku} not found`, {
        extensions: { code: 'BAD_INPUT' },
      });
    }
    return serviceType;
  };

  public abstract calculatePrice(input: TQuickPriceInput, calcOptions?: TCalcOptions): TQuickPriceCalcOutput;

  public getFreeStyleLineItem(itemCount: number, price: number, isPricesNet: boolean, taxTypeId: number) {
    const taxRate = this.taxRateSelector?.getTaxRate({
      country: this.hotel!.businessCountry,
      date: new Date(),
      taxTypeId: taxTypeId,
    });
    if (!taxRate)
      throw new QuickPriceError(`No tax rate found for ${taxTypeId}`, {
        extensions: { code: 'BAD_PRICELIST', taxTypeId },
      });

    if (isPricesNet) {
      const priceNet = itemCount * price;
      const tax = {
        type: this.taxRateSelector?.getTaxType(taxTypeId)!,
        rate: taxRate,
        price: roundPrice(priceNet * taxRate.rate),
      };
      const priceGross = priceNet + tax.price;
      return {
        priceNet,
        priceGross,
        components: [
          {
            type: tax.type,
            price: price,
          },
        ],
        taxes: [tax],
      };
    } else {
      const priceGross = itemCount * price;
      const priceNet = roundPrice(priceGross / (1 + taxRate.rate));
      const tax = {
        type: this.taxRateSelector?.getTaxType(taxTypeId)!,
        rate: taxRate,
        price: priceGross - priceNet,
      };
      return {
        priceNet,
        priceGross,
        priceItem: price,
        components: [
          {
            type: tax.type,
            price: price,
          },
        ],
        taxes: [tax],
      };
    }
  }

  public getLookupCodeForSKU(sku: string) {
    return (
      this.allBundlesDb?.find(b => b.sku === sku)?.lookupCode ||
      this.allProductsDb?.find(p => p.sku === sku)?.lookupCode ||
      this.allFacilitiesDb?.find(f => f.sku === sku)?.lookupCode
    );
  }
  public getLookupCodesForSKUs(skus: string[]) {
    return skus.map(sku => this.getLookupCodeForSKU(sku));
  }

  public getSKUForLookupCode(code: string) {
    return (
      this.allBundlesDb?.find(b => b.lookupCode === code)?.sku ||
      this.allProductsDb?.find(p => p.lookupCode === code)?.sku ||
      this.allFacilitiesDb?.find(f => f.lookupCode === code)?.sku
    );
  }
  public getSKUsForLookupCode(code: string) {
    return [
      ...(this.allBundlesDb || []).filter(b => b.lookupCode === code),
      ...(this.allProductsDb || []).filter(p => p.lookupCode === code),
      ...(this.allFacilitiesDb || []).filter(f => f.lookupCode === code),
    ].map(s => s.sku);
  }
  public getSKUsForLookupCodes(codes: string[]) {
    return _.uniq(codes.map(code => this.getSKUsForLookupCode(code)).reduce((agg, s) => [...agg, ...s], []));
  }

  private normalizeProductLabel(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private getFullBoardLabelKeys(product: IProduct): string[] {
    const keys = new Set<string>();

    const name = this.normalizeProductLabel(product.name);
    if (name) keys.add(`name:${name}`);

    const hotelId = this.hotel?.id ?? null;
    for (const language of ['de', 'en', null]) {
      for (const position of ['HEADER_SHORT', 'HEADER']) {
        try {
          const text = this.productTextSelector?.getProductText({
            sku: product.sku,
            hotelId,
            position,
            language,
          });
          const normalized = this.normalizeProductLabel(text);
          if (normalized) keys.add(`header:${normalized}`);
        } catch {
          // Product text may be incomplete in some hydrate payloads.
        }
      }
    }

    return Array.from(keys);
  }

  private sharesFullBoardLabel(a: IProduct, b: IProduct): boolean {
    const aKeys = this.getFullBoardLabelKeys(a);
    if (aKeys.length === 0) return false;
    const bKeys = new Set(this.getFullBoardLabelKeys(b));
    return aKeys.some(key => bKeys.has(key));
  }

  /**
   * Resolve which Vollpension lookupCode a product belongs to.
   * Catalogue (null lookupCode) may attach only when it uniquely matches EZ or DZ — never both.
   */
  private resolveFullBoardLookupCode(product: IProduct): string | null {
    if (product.lookupCode && FULL_BOARD_COMBINATION_LOOKUP_CODES.includes(product.lookupCode)) {
      return product.lookupCode;
    }
    if (product.lookupCode) return null;

    const products = this.allProductsDb || [];
    const matchingCodes = FULL_BOARD_COMBINATION_LOOKUP_CODES.filter(code => {
      const seeds = products.filter(p => p.lookupCode === code);
      return seeds.some(vp => this.sharesFullBoardLabel(product, vp));
    });
    return matchingCodes.length === 1 ? matchingCodes[0] : null;
  }

  private resolvePriceListId(params: { date: Date; priceListId?: number | null }): number | null {
    try {
      const priceList =
        (params.priceListId
          ? this.priceListSelector?.findPriceList({ priceListId: params.priceListId, date: params.date })
          : null) || this.getPriceList({ date: params.date });
      return priceList?.id ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Hierarchy can expose multiple products with the same lookupCode (ancestor + local).
   * Prices are stored per productId, so prefer a candidate that actually has a price.
   */
  public hasProductPrice(
    productId: number,
    params: { date: Date; guestCount?: number | null; itemCount?: number; isPremium?: boolean; publicCompanyId?: number; priceListId?: number | null },
  ): boolean {
    try {
      const priceListId = this.resolvePriceListId(params);
      if (!priceListId || !this.priceSelector) return false;

      // Prefer any configured price row for this productId (ignore guest-count filters).
      if (typeof this.priceSelector.hasPriceForProduct === 'function' && this.priceSelector.hasPriceForProduct(priceListId, productId)) {
        return true;
      }

      const price = this.priceSelector.findPrice({
        priceListId,
        productId,
        guestCount: params.guestCount ?? 0,
        itemCount: params.itemCount ?? 1,
        date: params.date,
        isPremium: params.isPremium,
        publicCompanyId: params.publicCompanyId,
      });
      return !!price && price.price !== null && price.price !== NO_PRICE;
    } catch {
      return false;
    }
  }

  private hasHotelCmsText(product: IProduct): boolean {
    const hotelId = this.hotel?.id;
    if (!hotelId || !this.productTextSelector) return false;
    if (typeof this.productTextSelector.hasHotelProductText === 'function') {
      return this.productTextSelector.hasHotelProductText(product.sku, hotelId);
    }
    return false;
  }

  /** Prefer priced twin for price resolution (SM-2581). */
  private preferPricedFullBoardProduct(
    candidates: IProduct[],
    params: { date: Date; guestCount?: number | null; itemCount?: number; isPremium?: boolean; publicCompanyId?: number; priceListId?: number | null },
    preferred?: IProduct,
  ): IProduct {
    const ordered = preferred
      ? [preferred, ...candidates.filter(c => c.id !== preferred.id)]
      : candidates;
    const withLookup = ordered.filter(c => c.lookupCode && FULL_BOARD_COMBINATION_LOOKUP_CODES.includes(c.lookupCode));
    const searchOrder = [...withLookup, ...ordered.filter(c => !withLookup.some(w => w.id === c.id))];
    return searchOrder.find(c => this.hasProductPrice(c.id, params)) || withLookup[0] || ordered[0];
  }

  /** Prefer hotel-labelled / priced twin in addon menus (hierarchy can expose ancestor + local). */
  private preferAddonMenuProduct(
    candidates: IProduct[],
    params: { date: Date; guestCount?: number | null; itemCount?: number; isPremium?: boolean; publicCompanyId?: number; priceListId?: number | null },
    preferLookupCodes?: string[],
  ): IProduct {
    const withLookup =
      preferLookupCodes && preferLookupCodes.length > 0
        ? candidates.filter(c => c.lookupCode && preferLookupCodes.includes(c.lookupCode))
        : [];
    const ordered = [...withLookup, ...candidates.filter(c => !withLookup.some(w => w.id === c.id))];
    const withHotelText = ordered.filter(c => this.hasHotelCmsText(c));

    const pricedWithHotel = withHotelText.find(c => this.hasProductPrice(c.id, params));
    if (pricedWithHotel) return pricedWithHotel;
    if (withHotelText[0]) return withHotelText[0];

    const priced = ordered.find(c => this.hasProductPrice(c.id, params));
    if (priced) return priced;

    return withLookup[0] || ordered[0];
  }

  /**
   * Twins for one Vollpension lookupCode only (EZ and DZ stay separate). Catalogue null-lookup
   * products attach only when they uniquely match that code (SM-2581).
   */
  private getFullBoardCandidates(product: IProduct): IProduct[] {
    const products = this.allProductsDb || [];
    const lookupCode = this.resolveFullBoardLookupCode(product);
    if (!lookupCode) return [product];

    const byLookup = products.filter(p => p.lookupCode === lookupCode);
    const catalogueTwins = products.filter(p => !p.lookupCode && this.resolveFullBoardLookupCode(p) === lookupCode);

    return _.uniqBy([...byLookup, ...catalogueTwins, product], p => p.id);
  }

  /**
   * If several hierarchy/catalogue products represent the same full-board combination,
   * return the SKU that has a price on the active list (SM-2581). Falls back to the requested SKU.
   */
  public resolvePricedProductSKU(
    sku: string,
    params: { date: Date; guestCount?: number | null; itemCount?: number; isPremium?: boolean; publicCompanyId?: number; priceListId?: number | null },
  ): string {
    const product = this.allProductsDb?.find(p => p.sku === sku);
    if (!product) return sku;

    const candidates = this.getFullBoardCandidates(product);
    if (candidates.length <= 1) return sku;

    return this.preferPricedFullBoardProduct(candidates, params, product).sku;
  }

  /** Prefer the priced hierarchy/catalogue twin for a full-board lookupCode (SM-2581). */
  public findPricedProductByLookupCode(
    lookupCode: string,
    params: { date: Date; guestCount?: number | null; itemCount?: number; isPremium?: boolean; publicCompanyId?: number; priceListId?: number | null },
  ): IProduct | undefined {
    const seed = (this.allProductsDb || []).find(p => p.lookupCode === lookupCode);
    if (!seed) return undefined;
    const resolvedSku = this.resolvePricedProductSKU(seed.sku, params);
    return (this.allProductsDb || []).find(p => p.sku === resolvedSku);
  }

  /**
   * Addon menus: one selectable row per service. Hierarchy can expose ancestor + local twins
   * with the same lookupCode or SKU (SM-2581 / SM-2779). Prefer hotel CMS label; full-board
   * price is remapped on add.
   */
  public getUniqueAddonProducts(params: {
    date: Date;
    guestCount?: number | null;
    itemCount?: number;
    isPremium?: boolean;
    publicCompanyId?: number;
    priceListId?: number | null;
  }) {
    const products = this.allProductsDb || [];
    const result: NonNullable<typeof this.allProductsDb> = [];
    const handledProductIds = new Set<number>();

    const pushChosen = (group: IProduct[], preferLookupCodes?: string[]) => {
      if (group.length === 0) return;
      const chosen = this.preferAddonMenuProduct(group, params, preferLookupCodes);
      if (!handledProductIds.has(chosen.id)) {
        result.push(chosen);
        handledProductIds.add(chosen.id);
      }
      for (const member of group) {
        handledProductIds.add(member.id);
        // Same SKU can exist on ancestor + local space even with mismatched lookupCode.
        for (const twin of products.filter(p => p.sku === member.sku)) {
          handledProductIds.add(twin.id);
        }
      }
    };

    // Full-board combinations: also collapse catalogue twins without lookupCode (SM-2581).
    for (const lookupCode of FULL_BOARD_COMBINATION_LOOKUP_CODES) {
      const seeds = products.filter(p => p.lookupCode === lookupCode);
      if (seeds.length === 0) continue;
      pushChosen(this.getFullBoardCandidates(seeds[0]), FULL_BOARD_COMBINATION_LOOKUP_CODES);
    }

    // Other shared lookupCodes across the space hierarchy (e.g. Dinner, rooms).
    const remainingWithLookup = products.filter(p => !handledProductIds.has(p.id) && p.lookupCode);
    for (const group of Object.values(_.groupBy(remainingWithLookup, p => p.lookupCode))) {
      pushChosen(group);
    }

    // Same SKU from ancestor + local space (unique per spaceId only).
    const remainingBySku = products.filter(p => !handledProductIds.has(p.id));
    for (const group of Object.values(_.groupBy(remainingBySku, p => p.sku))) {
      pushChosen(group);
    }

    return result;
  }

  /** One row per SKU for addon bundle/facility menus (hierarchy twins). */
  public getUniqueAddonBySku<T extends { id: number; sku: string }>(items: T[] | null | undefined): T[] {
    if (!items?.length) return [];
    return _.uniqBy(items, item => item.sku);
  }

  public getSKUIncludedInServiceType(sku: string, addons: boolean = false) {
    const serviceType = this.allServiceTypesDb?.find(s => s.sku === sku);
    if (!serviceType) return [];

    const serviceTypeIncludedSkus = _.uniq(
      [
        ...(addons ? serviceType.addons.map(a => (a.facility ? a.facility.sku : a.product ? a.product.sku : null)) : []),
        ...serviceType.facilities.map(f => f.facility.sku),
        ...serviceType.products.filter(p => p.product).map(p => p.product!.sku),
        ...serviceType.products
          .filter(p => p.bundle)
          .map(p => this.allBundlesDb?.find(b => b.id === p.bundle!.id))
          .filter(b => b)
          .reduce<string[]>(
            (agg, b) => [...agg, ...b!.bundleItems.map(bi => this.allProductsDb?.find(p => p.id === bi.productId)?.sku)].filter(s => s).map(s => s!),
            [],
          ),
      ]
        .filter(s => s)
        .map(s => s!),
    );
    return serviceTypeIncludedSkus;
  }
  public getSKUBundleItems(sku: string) {
    const bundle = this.allBundlesDb?.find(b => b.sku === sku);
    if (!bundle) return [];

    return bundle.bundleItems
      .map(bi => ({ includedCount: bi.includedCount, product: this.allProductsDb?.find(p => p.id === bi.productId) }))
      .filter(s => s.product && s.product.sku)
      .map(s => ({ includedCount: s.includedCount, sku: s.product!.sku!, lookupCode: s.product!.lookupCode }));
  }

  public getSKURequiredComponents(sku: string) {
    const bundle = this.allBundlesDb?.find(b => b.sku === sku);
    if (bundle) return bundle.components;

    const product = this.allProductsDb?.find(p => p.sku === sku);
    if (product) return product.components;

    const facility = this.allFacilitiesDb?.find(f => f.sku === sku);
    if (facility) return facility.components;

    return null;
  }

  public getTaxRateForType(taxTypeId: number) {
    const taxRate = this.taxRateSelector?.getTaxRate({
      country: this.hotel!.businessCountry,
      date: new Date(),
      taxTypeId: taxTypeId,
    });
    return taxRate || null;
  }
  public getSKULineItem(
    sku: string,
    params: {
      day?: number | null;
      date: Date;
      itemCount: number;
      guestCount?: number | null;
      isPremium?: boolean;
      publicCompanyId?: number;
      priceListId?: number | null;
    },
    price?: IPrice | null,
    calcOptions?: ICalcOptions | null,
  ) {
    // Resolve SKU once and delegate to the specialized pricing path.
    const bundle = this.allBundlesDb?.find(b => b.sku === sku);
    const product = this.allProductsDb?.find(p => p.sku === sku);
    const facility = this.allFacilitiesDb?.find(f => f.sku === sku);

    const priceList =
      (params.priceListId
        ? this.priceListSelector?.findPriceList({ priceListId: params.priceListId, date: params.date })
        : null) || this.getPriceList({ date: params.date });

    if (bundle) {
      return this.getBundleLineItem(
        params.day || 0,
        {
          priceListId: priceList.id,
          bundleId: bundle.id,
          date: params.date,
          guestCount: params.guestCount ?? 0,
          itemCount: params.itemCount,
          isPremium: params.isPremium,
          publicCompanyId: params.publicCompanyId,
        },
        price,
        calcOptions,
      );
    } else if (product) {
      return this.getProductLineItem(
        params.day || 0,
        {
          priceListId: priceList.id,
          productId: product.id,
          date: params.date,
          guestCount: params.guestCount ?? 0,
          itemCount: params.itemCount,
          isPremium: params.isPremium,
          publicCompanyId: params.publicCompanyId,
        },
        price,
        calcOptions,
      );
    } else if (facility) {
      return this.getFacilityLineItem(
        params.day || 0,
        {
          priceListId: priceList.id,
          facilityId: facility.id,
          date: params.date,
          guestCount: params.guestCount ?? 0,
          itemCount: params.itemCount,
          isPremium: params.isPremium,
          publicCompanyId: params.publicCompanyId,
        },
        price,
        calcOptions,
      );
    } else {
      throw new QuickPriceError(`No product found for SKU ${sku}`, {
        extensions: { code: 'BAD_PRODUCT', sku },
      });
    }
  }

  public getBundleLineItem(day: number, selInput: PriceSelectorInput, price?: IPrice | null, calcOptions?: ICalcOptions | null) {
    const bundle = this.allBundlesDb?.find(b => b.id === selInput.bundleId);
    if (!bundle) return null;

    if (!price && calcOptions && calcOptions.priceSelector) {
      price = calcOptions.priceSelector(selInput);
    }
    if (!price && this.priceSelector) {
      price = this.priceSelector.findPrice(selInput);
    }
    // Strict mode requires a concrete price; permissive mode can propagate NO_PRICE.
    if (!price && !calcOptions?.allowNoPrices)
      throw new QuickPriceError(`No price found for bundle ${bundle.name}`, {
        extensions: { code: 'BAD_PRICELIST', bundle: bundle.name },
      });

    const priceList = this.getPriceList({
      date: selInput.date,
      priceListId: selInput.priceListId,
    });

    const isPricesNet = calcOptions?.isPricesNet ?? priceList.isPricesNet;

    // Some bundles inherit pricing from contained products instead of a standalone bundle price.
    if (price && price.bundlePriceFromProduct) {
      const productLineItems = bundle.bundleItems
        .map(bi => {
          try {
            return this.getProductLineItem(
              day,
              {
                ...selInput,
                itemCount: bi.includedCount,
                bundleId: undefined,
                productId: bi.productId,
                selectorId: price?.selector?.id,
              },
              null,
              calcOptions,
            );
          } catch (err: any) {
            console.warn(err.message);
            return null;
          }
        })
        .filter(pli => pli) as ICalcLineItem[];
      const lineItem: ICalcLineItem = {
        day: day,
        sku: bundle.sku,
        name: bundle.name,
        bundle,
        selector: price.selector,
        count: selInput.itemCount,
        isIncluded: false,
        priceItem: productLineItems.find(pli => pli.priceItem === NO_PRICE)
          ? NO_PRICE
          : productLineItems.reduce((s, pli) => s + pli.priceItem * pli.count, 0),
        priceGross: 0,
        priceNet: productLineItems.find(pli => pli.priceNet === NO_PRICE)
          ? NO_PRICE
          : productLineItems.reduce((s, pli) => s + pli.priceNet, 0) * selInput.itemCount,
        components: [],
        taxes: [],
      };
      const productLineItemTaxes = productLineItems.reduce(
        (s, pli) => [
          ...s,
          ...pli.taxes.map(t => ({
            ...t,
            price: t.price === NO_PRICE ? NO_PRICE : t.price * selInput.itemCount,
          })),
        ],
        [] as ICalcTax[],
      );
      // Preserve NO_PRICE sentinel when any contributing element cannot be priced.
      lineItem.priceGross =
        lineItem.priceNet === NO_PRICE || productLineItemTaxes.find(t => t.price === NO_PRICE)
          ? NO_PRICE
          : lineItem.priceNet + productLineItemTaxes.reduce((agg, t) => agg + t.price, 0);
      lineItem.taxes = mergeTaxes([], productLineItemTaxes);

      const productLineItemComponents = productLineItems.reduce(
        (s, pli) => [
          ...s,
          ...pli.components.map(plic => ({
            type: plic.type,
            price: plic.price === NO_PRICE ? NO_PRICE : plic.price * pli.count,
          })),
        ],
        [] as ICalcPriceComponent[],
      );
      lineItem.components = mergeComponents([], productLineItemComponents);

      return lineItem;
    } else {
      const lineItem: ICalcLineItem = {
        day: day,
        sku: bundle.sku,
        name: bundle.name,
        bundle,
        selector: price?.selector,
        count: selInput.itemCount,
        isIncluded: false,
        priceItem: price ? price.price || 0 : NO_PRICE,
        priceGross: 0,
        priceNet: 0,
        components: [],
        taxes: [],
      };

      // Normalize single-component prices so downstream logic can stay component-based.
      if (price && bundle.components.length === 1) {
        price.components = [
          {
            price: price.price || 0,
            taxTypeId: bundle.components[0].taxTypeId,
          },
        ];
      }

      for (const bc of bundle.components) {
        if (lineItem.components.find(c => c.type.id === bc.taxTypeId)) continue;

        const taxType = this.taxRateSelector?.getTaxType(bc.taxTypeId);
        const taxRate = this.taxRateSelector?.getTaxRate({
          country: this.hotel!.businessCountry,
          date: selInput.date,
          taxTypeId: bc.taxTypeId,
        });
        if (!taxRate || !taxType) {
          throw new QuickPriceError(`No tax rate found for bundle ${bundle.name}/${taxType?.name}`, {
            extensions: {
              code: 'BAD_PRICELIST',
              bundle: bundle.name,
              taxType: taxType?.name,
            },
          });
        }

        if (price) {
          const taxComponent = price.components?.find(c => c.taxTypeId === bc.taxTypeId);
          const taxComponentBasePrice = taxComponent ? taxComponent.price : 0;
          lineItem.components.push({
            type: taxType,
            price: taxComponentBasePrice,
          });

          if (taxComponentBasePrice !== NO_PRICE) {
            if (isPricesNet) {
              lineItem.taxes.push({
                type: taxType,
                rate: taxRate,
                price: selInput.itemCount * roundPrice(taxComponentBasePrice * taxRate.rate),
              });
            } else {
              lineItem.taxes.push({
                type: taxType,
                rate: taxRate,
                price: selInput.itemCount * roundPrice(taxComponentBasePrice - taxComponentBasePrice / (1 + taxRate.rate)),
              });
            }
          } else {
            lineItem.taxes.push({
              type: taxType,
              rate: taxRate,
              price: NO_PRICE,
            });
          }
        } else {
          lineItem.components.push({
            type: taxType,
            price: NO_PRICE,
          });
          lineItem.taxes.push({
            type: taxType,
            rate: taxRate,
            price: NO_PRICE,
          });
        }
      }
      lineItem.taxes = mergeTaxes([], lineItem.taxes);
      lineItem.components = mergeComponents([], lineItem.components);

      // Net/gross conversion depends on pricelist mode; sentinel values short-circuit totals.
      if (price && price.price !== NO_PRICE && !lineItem.taxes.find(t => t.price === NO_PRICE)) {
        if (isPricesNet) {
          lineItem.priceNet = selInput.itemCount * (price.price || 0);
          lineItem.priceGross = lineItem.priceNet + lineItem.taxes.reduce((sum, t) => sum + t.price, 0);
        } else {
          lineItem.priceGross = selInput.itemCount * (price.price || 0);
          lineItem.priceNet = lineItem.priceGross - lineItem.taxes.reduce((sum, t) => sum + t.price, 0);
        }
      } else {
        lineItem.priceNet = NO_PRICE;
        lineItem.priceGross = NO_PRICE;
      }
      return lineItem;
    }
  }

  public getProductLineItem(day: number, selInput: PriceSelectorInput, price?: IPrice | null, calcOptions?: ICalcOptions | null) {
    const product = this.allProductsDb?.find(p => p.id === selInput.productId);
    if (!product) return null;

    if (!price && calcOptions && calcOptions.priceSelector) {
      price = calcOptions.priceSelector(selInput);
    }
    if (!price && this.priceSelector) {
      price = this.priceSelector.findPrice(selInput);
    }
    if (!price && !calcOptions?.allowNoPrices)
      throw new QuickPriceError(`No price found for product ${product.name}`, {
        extensions: { code: 'BAD_PRICELIST', product: product.name },
      });

    const priceList = this.getPriceList({
      date: selInput.date,
      priceListId: selInput.priceListId,
    });
    const isPricesNet = calcOptions?.isPricesNet ?? priceList.isPricesNet;

    const lineItem: ICalcLineItem = {
      day: day,
      sku: product.sku,
      name: product.name,
      product,
      selector: price?.selector,
      count: selInput.itemCount,
      isIncluded: false,
      priceItem: price ? price.price || 0 : NO_PRICE,
      priceGross: 0,
      priceNet: 0,
      components: [],
      taxes: [],
    };

    // Deductions are represented as negative line items.
    const multi = product.isDeduction ? -1 : 1;

    if (price && product.components.length === 1) {
      price.components = [
        {
          price: price.price || 0,
          taxTypeId: product.components[0].taxTypeId,
        },
      ];
    }

    for (const bc of product.components) {
      if (lineItem.components.find(c => c.type.id === bc.taxTypeId)) continue;

      const taxType = this.taxRateSelector?.getTaxType(bc.taxTypeId);
      const taxRate = this.taxRateSelector?.getTaxRate({
        country: this.hotel!.businessCountry,
        date: selInput.date,
        taxTypeId: bc.taxTypeId,
      });
      if (!taxRate || !taxType) {
        throw new QuickPriceError(`No tax rate found for product ${product.name}/${taxType?.name}`, {
          extensions: {
            code: 'BAD_PRICELIST',
            product: product.name,
            taxType: taxType?.name,
          },
        });
      }

      if (price) {
        const taxComponent = price.components?.find(c => c.taxTypeId === bc.taxTypeId);
        const taxComponentBasePrice = taxComponent ? taxComponent.price : 0;
        lineItem.components.push({
          type: taxType,
          price: taxComponentBasePrice,
        });

        if (taxComponentBasePrice !== NO_PRICE) {
          if (isPricesNet) {
            lineItem.taxes.push({
              type: taxType,
              rate: taxRate,
              price: multi * selInput.itemCount * roundPrice(taxComponentBasePrice * taxRate.rate),
            });
          } else {
            lineItem.taxes.push({
              type: taxType,
              rate: taxRate,
              price: selInput.itemCount * roundPrice(taxComponentBasePrice - taxComponentBasePrice / (1 + taxRate.rate)),
            });
          }
        } else {
          lineItem.taxes.push({
            type: taxType,
            rate: taxRate,
            price: NO_PRICE,
          });
        }
      } else {
        lineItem.components.push({
          type: taxType,
          price: NO_PRICE,
        });
        lineItem.taxes.push({
          type: taxType,
          rate: taxRate,
          price: NO_PRICE,
        });
      }
    }
    lineItem.taxes = mergeTaxes([], lineItem.taxes);
    lineItem.components = mergeComponents([], lineItem.components);

    if (price && price.price !== NO_PRICE && !lineItem.taxes.find(t => t.price === NO_PRICE)) {
      if (isPricesNet) {
        lineItem.priceNet = multi * selInput.itemCount * (price.price || 0);
        lineItem.priceGross = lineItem.priceNet + lineItem.taxes.reduce((sum, t) => sum + t.price, 0) * multi;
      } else {
        lineItem.priceGross = multi * selInput.itemCount * (price.price || 0);
        lineItem.priceNet = lineItem.priceGross - lineItem.taxes.reduce((sum, t) => sum + t.price, 0) * multi;
      }
    } else {
      lineItem.priceNet = NO_PRICE;
      lineItem.priceGross = NO_PRICE;
    }
    return lineItem;
  }

  public getFacilityLineItem(day: number, selInput: PriceSelectorInput, price?: IPrice | null, calcOptions?: ICalcOptions | null) {
    const facility = this.allFacilitiesDb?.find(f => f.id === selInput.facilityId);
    if (!facility) return null;

    if (!price && calcOptions && calcOptions.priceSelector) {
      price = calcOptions.priceSelector(selInput);
    }
    if (!price && this.priceSelector) {
      price = this.priceSelector.findPrice(selInput);
    }
    if (!price && !calcOptions?.allowNoPrices)
      throw new QuickPriceError(`No price found for facility ${facility.name}`, {
        extensions: { code: 'BAD_PRICELIST', facility: facility.name },
      });

    const priceList = this.getPriceList({
      date: selInput.date,
      priceListId: selInput.priceListId,
    });
    const isPricesNet = calcOptions?.isPricesNet ?? priceList.isPricesNet;

    // Validate tax coverage early to fail with a precise facility/tax context.
    const taxRatesToApply: ITaxRate[] = [];

    for (const c of facility.components) {
      const taxRate = this.taxRateSelector?.getTaxRate({
        country: this.hotel!.businessCountry,
        date: selInput.date,
        taxTypeId: c.taxTypeId,
      });
      if (!taxRate) {
        const taxType = this.taxRateSelector?.getTaxType(c.taxTypeId)!;
        throw new QuickPriceError(`No tax rate found for facility ${facility.name}/${taxType.name}`, {
          extensions: {
            code: 'BAD_PRICELIST',
            facility: facility.name,
            taxType: taxType.name,
          },
        });
      }
      taxRatesToApply.push(taxRate);
    }

    const lineItem: ICalcLineItem = {
      day: day,
      sku: facility.sku,
      name: facility.name,
      facility,
      selector: price?.selector,
      count: selInput.itemCount,
      isIncluded: false,
      priceItem: price ? price.price || 0 : NO_PRICE,
      priceGross: 0,
      priceNet: 0,
      components: [],
      taxes: [],
    };

    if (price && facility.components.length === 1) {
      price.components = [
        {
          price: price.price || 0,
          taxTypeId: facility.components[0].taxTypeId,
        },
      ];
    }

    for (const bc of facility.components) {
      if (lineItem.components.find(c => c.type.id === bc.taxTypeId)) continue;

      const taxType = this.taxRateSelector?.getTaxType(bc.taxTypeId);
      const taxRate = this.taxRateSelector?.getTaxRate({
        country: this.hotel!.businessCountry,
        date: selInput.date,
        taxTypeId: bc.taxTypeId,
      });
      if (!taxRate || !taxType) {
        throw new QuickPriceError(`No tax rate found for facility ${facility.name}/${taxType?.name}`, {
          extensions: {
            code: 'BAD_PRICELIST',
            facility: facility.name,
            taxType: taxType?.name,
          },
        });
      }
      if (price) {
        const taxComponent = price.components?.find(c => c.taxTypeId === bc.taxTypeId);
        const taxComponentBasePrice = taxComponent ? taxComponent.price : 0;

        lineItem.components.push({
          type: taxType,
          price: taxComponentBasePrice,
        });

        if (taxComponentBasePrice !== NO_PRICE) {
          if (isPricesNet) {
            lineItem.taxes.push({
              type: taxType,
              rate: taxRate,
              price: selInput.itemCount * roundPrice(taxComponentBasePrice * taxRate.rate),
            });
          } else {
            lineItem.taxes.push({
              type: taxType,
              rate: taxRate,
              price: selInput.itemCount * roundPrice(taxComponentBasePrice - taxComponentBasePrice / (1 + taxRate.rate)),
            });
          }
        } else {
          lineItem.taxes.push({
            type: taxType,
            rate: taxRate,
            price: NO_PRICE,
          });
        }
      } else {
        lineItem.components.push({
          type: taxType,
          price: NO_PRICE,
        });
        lineItem.taxes.push({
          type: taxType,
          rate: taxRate,
          price: NO_PRICE,
        });
      }
    }
    lineItem.taxes = mergeTaxes([], lineItem.taxes);
    lineItem.components = mergeComponents([], lineItem.components);

    if (price && price.price !== NO_PRICE && !lineItem.taxes.find(t => t.price === NO_PRICE)) {
      if (isPricesNet) {
        lineItem.priceNet = selInput.itemCount * (price.price || 0);
        lineItem.priceGross = lineItem.priceNet + lineItem.taxes.reduce((sum, t) => sum + t.price, 0);
      } else {
        lineItem.priceGross = selInput.itemCount * (price.price || 0);
        lineItem.priceNet = lineItem.priceGross - lineItem.taxes.reduce((sum, t) => sum + t.price, 0);
      }
    } else {
      lineItem.priceNet = NO_PRICE;
      lineItem.priceGross = NO_PRICE;
    }
    return lineItem;
  }

  public getLineItemsContent(calcOutput: ICalcPriceOutput, language: string) {
    const lineItemContent: {
      [sku: string]: { header: string | null; details: string | null };
    } = {};

    for (const li of calcOutput.lineItems) {
      lineItemContent[li.sku] = this.getLineItemContent(calcOutput, li, language);
    }
    return lineItemContent;
  }

  public getLineItemContent(calcOutput: ICalcPriceOutput, lineItem: ICalcLineItem, language: string) {
    const result: { header: string | null; details: string | null } = { header: null, details: null };
    if (this.productTextSelector) {
      result.header =
        this.productTextSelector.getProductText({
          sku: lineItem.sku,
          hotelId: this.hotel!.id,
          position: 'HEADER',
          language: language,
        }) || lineItem.name;
    }
    if (this.productTextSelector) {
      result.details =
        this.productTextSelector.getProductText({
          sku: lineItem.sku,
          hotelId: this.hotel!.id,
          position: 'DETAILS',
          language: language,
        }) || '';
    }
    return result;
  }
}
